import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PatientService from "services/PatientService";
import { selectHospital } from "state/slices/hospitalSlice";

function GlobalPatientSearch() {
  const navigate = useNavigate();
  const focusedHospital = useSelector(selectHospital);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  const canSearch = useMemo(() => focusedHospital.id !== "", [focusedHospital.id]);

  useEffect(() => {
    if (!canSearch || inputValue.trim().length < 2) {
      setOptions([]);
      return;
    }

    const timerId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await PatientService.getAllPatients([
          focusedHospital.id,
          0,
          8,
          inputValue.trim(),
        ]);
        setOptions(response.data?.content || []);
      } catch (error) {
        console.error("Failed to search patients", error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timerId);
  }, [canSearch, focusedHospital.id, inputValue]);

  return (
    <Autocomplete
      size="small"
      sx={{ width: { xs: 210, md: 320 } }}
      options={options}
      loading={loading}
      autoComplete
      includeInputInList
      openOnFocus
      filterOptions={(x) => x}
      getOptionLabel={(option) => `${option.firstName || ""} ${option.lastName || ""}`.trim()}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body2">
              {`${option.firstName || ""} ${option.lastName || ""}`.trim() || "Unnamed Patient"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Patient ID: {option.patientId || option.id || "-"}
            </Typography>
          </Box>
        </Box>
      )}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onInputChange={(_, value) => setInputValue(value)}
      onChange={(_, value) => {
        if (!value?.id) return;
        navigate(`/hospital/emr-management/patients/${value.id}/profile`);
      }}
      noOptionsText={
        inputValue.trim().length < 2
          ? "Type at least 2 letters to search by name or email"
          : "No patients found. Try full name, email, or a different spelling."
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search patients"
          placeholder="Search by patient name or email"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default GlobalPatientSearch;
